import React, { useEffect, useState, useRef } from "react";
import axios from "axios";
import API_BASE_URL from "../config/apiConfig";
import {
  Tab,
  Tabs,
  Spinner,
  Row,
  Col,
  Card,
  Button,
  OverlayTrigger,
  Popover,
  ListGroup,
} from "react-bootstrap";
import { toast } from "react-toastify";
import "../css/TripHistoryOverlay.css";
import { formatDateDMY } from "../utils/dateFormat";

const TripHistoryOverlay = ({ onClose }) => {
  const [pending, setPending] = useState([]);
  const [upcoming, setUpcoming] = useState([]);
  const [ongoing, setOngoing] = useState([]);
  const [completed, setCompleted] = useState([]);
  const [cancelled, setCancelled] = useState([]);
  const [loading, setLoading] = useState(true);
  const contentRef = useRef();

  const handleOverlayClick = (e) => {
    if (contentRef.current && !contentRef.current.contains(e.target)) {
      onClose();
    }
  };

  useEffect(() => {
    const storedUser = localStorage.getItem("user");
    if (!storedUser) {
      onClose();
      return;
    }

    const user = JSON.parse(storedUser);
    const fetchTrips = async () => {
      try {
        const [pen, up, active, comp, cancel] = await Promise.all([
          axios.get(`${API_BASE_URL}/api/bookings/${user.id}/PENDING`),
          axios.get(`${API_BASE_URL}/api/bookings/${user.id}/UPCOMING`),
          axios.get(`${API_BASE_URL}/api/bookings/${user.id}/ONGOING`),
          axios.get(`${API_BASE_URL}/api/bookings/${user.id}/COMPLETED`),
          axios.get(`${API_BASE_URL}/api/bookings/${user.id}/CANCELLED`),
        ]);
        setPending(pen.data);
        setUpcoming(up.data);
        setOngoing(active.data);
        setCompleted(comp.data);
        setCancelled(cancel.data);
      } catch (err) {
        console.error("Failed to load trip history", err);
      } finally {
        setLoading(false);
      }
    };

    fetchTrips();
  }, [onClose]);

  const currency = (n) => `₹${Number(n || 0).toLocaleString()}`;

  const trackingUrl = (booking) => {
    const token = booking.trackingPublicToken || booking.tracking_public_token;
    return token ? `${window.location.origin}/tracking/${token}` : "";
  };

  const copyTrackingLink = async (url) => {
    if (!url) return;
    await navigator.clipboard?.writeText(url);
    toast.success("Tracking link copied");
  };

  const summarize = (b) => {
    const type = (b.bookingType || b.booking_type || 'PACKAGE').toUpperCase();
    const unit = type === 'CUSTOM'
      ? Number(b.pricePerPerson ?? b.price_per_person ?? 0)
      : Number(b.priceList?.price ?? b.price_list?.price ?? 0);
    const currencyCode = type === 'CUSTOM'
      ? (b.priceList?.currencyCode || b.price_list?.currencyCode || 'INR')
      : (b.priceList?.currencyCode || b.price_list?.currencyCode || 'INR');
    const travellers = Number(b.noOfTravellers ?? (b.travellers?.length ?? 0) ?? 0);
    const subtotal = unit * travellers;
    // Prefer explicit discount amount if backend returns one
    const discount = Number(
      b.couponDiscount ?? b.discountAmount ?? b.discount_amount ?? 0
    ) || 0;
    const advance = Number(b.advancePaid ?? b.advance_paid ?? 0) || 0;
    const total = Math.max(0, subtotal - discount);
    const balance = Math.max(0, total - advance);
    return { type, unit, travellers, subtotal, discount, advance, total, balance, currencyCode };
  };

  const renderTrips = (bookings) => {
    if (!Array.isArray(bookings) || bookings.length === 0) {
      return <p>No trips found in this category.</p>;
    }

    return (
      <Row>
        {bookings.map((booking, idx) => {
          const travellerPopover = (
            <Popover id={`popover-${idx}`}>
              <Popover.Header as="h3">Traveller Details</Popover.Header>
              <Popover.Body>
                {(booking.travellers || []).map((t, i) => (
                  <div key={i}>
                    👤 {t.name}, Age: {t.age}, 📞 {t.phone}
                  </div>
                ))}
              </Popover.Body>
            </Popover>
          );

          const s = summarize(booking);
          const driver = booking.driver || booking.assignedDriver || null;
          const d = driver || {};
          const driverName = d.driverName || d.name || booking.driverName || booking.driver_name || null;
          const driverPhone = d.driverPhone || d.phone || booking.driverPhone || booking.driver_phone || null;
          const yearsExp = d.yearsExperience ?? d.experience ?? booking.yearsExperience ?? booking.years_experience ?? null;
          const langs = Array.isArray(d.languages)
            ? d.languages
            : (d.languages || booking.languages || booking.driver_languages || "").toString().split(/[,\s]+/).filter(Boolean);
          const dlFront = d.dlFrontUrl || d.dl_front_image || d.dlFront || booking.dlFront || booking.dl_front_image || null;
          const dlBack = d.dlBackUrl || d.dl_back_image || d.dlBack || booking.dlBack || booking.dl_back_image || null;
          const publicTrackingUrl = trackingUrl(booking);

          return (
            <Col md={4} key={idx} className="mb-4">
              <Card className="h-100 shadow-sm">
                <Card.Img
                  variant="top"
                  src={booking.trip.imageUrl}
                  style={{ height: "200px", objectFit: "cover" }}
                />
                <Card.Body>
                  <Card.Title>{booking.trip.title}</Card.Title>
                  <Card.Text className="mb-1">⏳ {booking.bookingDuration || booking.trip.duration}</Card.Text>
                  <Card.Text>🗓️ Travel Date: {formatDateDMY(booking.travelDate)}</Card.Text>

                  <OverlayTrigger
                    trigger={["hover", "focus"]}
                    placement="top"
                    overlay={travellerPopover}
                  >
                    <Card.Text style={{ cursor: "pointer" }}>
                      👥 Travellers: {booking.noOfTravellers}
                    </Card.Text>
                  </OverlayTrigger>

                  <ListGroup className="mb-3">
                    {(booking.travellers || []).map((t, i) => (
                      <ListGroup.Item key={i} className="small">
                        👤 {t.name}, Age: {t.age}, 📞 {t.phone}
                      </ListGroup.Item>
                    ))}
                  </ListGroup>

                  {/* Driver Details (when assigned) */}
                  {driverName || driverPhone || yearsExp || (langs && langs.length) || dlFront || dlBack ? (
                    <Card className="mb-3 border-0 bg-light">
                      <Card.Body className="py-2">
                        <div className="fw-semibold mb-2">Driver Details</div>
                        {driverName && (
                          <div className="small mb-1">👤 {driverName}{d.id ? ` (#${d.id})` : ''}</div>
                        )}
                        {driverPhone && (
                          <div className="small text-muted mb-1">📞 {driverPhone}</div>
                        )}
                        {(yearsExp != null) && (
                          <div className="small mb-1">🧰 Experience: {yearsExp} yrs</div>
                        )}
                        {langs && langs.length ? (
                          <div className="small mb-2">🗣️ Languages: {langs.join(', ')}</div>
                        ) : null}
                        {(dlFront || dlBack) && (
                          <div className="d-flex gap-2 flex-wrap">
                            {dlFront && (
                              <a href={dlFront} target="_blank" rel="noreferrer" className="text-decoration-none">
                                <img
                                  src={dlFront}
                                  alt="DL Front"
                                  style={{ width: 120, height: 80, objectFit: 'cover', borderRadius: 6, border: '1px solid #eee' }}
                                  onError={(e) => { e.currentTarget.style.display = 'none'; }}
                                />
                                <div className="small text-muted text-center">DL Front</div>
                              </a>
                            )}
                            {dlBack && (
                              <a href={dlBack} target="_blank" rel="noreferrer" className="text-decoration-none">
                                <img
                                  src={dlBack}
                                  alt="DL Back"
                                  style={{ width: 120, height: 80, objectFit: 'cover', borderRadius: 6, border: '1px solid #eee' }}
                                  onError={(e) => { e.currentTarget.style.display = 'none'; }}
                                />
                                <div className="small text-muted text-center">DL Back</div>
                              </a>
                            )}
                          </div>
                        )}
                      </Card.Body>
                    </Card>
                  ) : null}

                  {/* Price Summary */}
                  <Card className="mb-3 border-0 bg-light">
                    <Card.Body className="py-2">
                      <div className="fw-semibold mb-1">Price Summary</div>
                      <div className="d-flex justify-content-between small">
                        <span>Unit Price{s.type === 'CUSTOM' ? ' (Custom)' : ''}</span>
                        <span>{currency(s.unit)} {s.type === 'CUSTOM' ? '' : (s.currencyCode || '')}</span>
                      </div>
                      <div className="d-flex justify-content-between small">
                        <span>Travellers</span>
                        <span>{s.travellers}</span>
                      </div>
                      <div className="d-flex justify-content-between small fw-semibold">
                        <span>Subtotal</span>
                        <span>{currency(s.subtotal)}</span>
                      </div>
                      {(booking.couponCode || booking.coupon_code || s.discount > 0) && (
                        <div className="d-flex justify-content-between small">
                          <span>Coupon {booking.couponCode || booking.coupon_code ? `(${booking.couponCode || booking.coupon_code})` : ''}</span>
                          <span className={s.discount ? 'text-success' : 'text-muted'}>
                            - {currency(s.discount)}
                          </span>
                        </div>
                      )}
                      <div className="d-flex justify-content-between small">
                        <span>Advance Paid</span>
                        <span className={s.advance ? 'text-success' : 'text-muted'}>
                          - {currency(s.advance)}
                        </span>
                      </div>
                      <hr className="my-2" />
                      <div className="d-flex justify-content-between fw-semibold">
                        <span>Balance Due</span>
                        <span>{currency(s.balance)}</span>
                      </div>
                    </Card.Body>
                  </Card>

                  <Button
                    variant="outline-primary"
                    className="w-100"
                    onClick={onClose}
                    as="a"
                    href={`/trips/${booking.trip.path}`}
                  >
                    View Trip
                  </Button>
                  {publicTrackingUrl && (
                    <div className="d-grid gap-2 mt-2">
                      <Button
                        variant="primary"
                        as="a"
                        href={publicTrackingUrl}
                        target="_blank"
                        rel="noreferrer"
                      >
                        View Tracking
                      </Button>
                      <Button
                        variant="outline-secondary"
                        onClick={() => copyTrackingLink(publicTrackingUrl)}
                      >
                        Copy Tracking Link
                      </Button>
                    </div>
                  )}
                </Card.Body>
              </Card>
            </Col>
          );
        })}
      </Row>
    );
  };

  return (
    <div className="trip-history-overlay" onClick={handleOverlayClick}>
      <div className="trip-history-content" ref={contentRef}>
        <Button variant="danger" className="close-btn" onClick={onClose}>
          &times;
        </Button>
        <h3 className="text-center mb-3">My Bookings</h3>

        {loading ? (
          <div className="text-center">
            <Spinner animation="border" />
          </div>
        ) : (
          <Tabs defaultActiveKey="upcoming" className="mb-3" fill>
            <Tab eventKey="pending" title="Pending">
              {renderTrips(pending)}
            </Tab>
            <Tab eventKey="upcoming" title="Upcoming">
              {renderTrips(upcoming)}
            </Tab>
            <Tab eventKey="ongoing" title="Ongoing">
              {renderTrips(ongoing)}
            </Tab>
            <Tab eventKey="completed" title="Completed">
              {renderTrips(completed)}
            </Tab>
            <Tab eventKey="cancelled" title="Cancelled">
              {renderTrips(cancelled)}
            </Tab>
          </Tabs>
        )}
      </div>
    </div>
  );
};

export default TripHistoryOverlay;
